import { useMemo, useState, useCallback } from 'react';
import { ICommonContextsProps } from '@ldp/common-contexts';
import { useSDMCartContext, useSDMCustomerContext } from '@ldp/sdm-contexts';
import { useCookieConsent, useWindowSize, useDetermineLocale, useGetKameleoonFlag } from '@/hooks';
import { trackEvent, trackEventTileAdClick, trackRender } from '@/utils/analytics/analytics-utils';
import { KAMELEOON_FEATURE_FLAGS } from '@/utils/kameleoon/constants';
import { getSiteId } from '@/utils';
import { isLoggedIn } from 'Utils/auth';
import UniversalCookies from 'universal-cookie';

/**
 * Hook to retrieve the common contexts props.
 */
export const useCommonContextProps = () => {
  const locale = useDetermineLocale();
  const { currentDevice } = useWindowSize({ isModernized: true });

  const { cartCount, pageId } = useSDMCartContext();
  const { pcOptimumAccount } = useSDMCustomerContext();

  const isSearchTypeaheadEnabled = useGetKameleoonFlag(KAMELEOON_FEATURE_FLAGS.ENABLE_SEARCH_TYPEAHEAD);

  const isQuebec = useCookieConsent();
  const [componentError, setComponentError] = useState({ mastheadError: '' });
  const cookieStore = new UniversalCookies();

  const siteId = getSiteId();
  const [cartId, setCartId] = useState<string | null>(null);
  

  const trackEventWithCartIdRetry = useCallback(
    (type: string, context: any) => {
      const snowplowGlobalContextFields = { cartCount, locale, currentDevice, pageId, pcOptimumAccount };

      // for logged in users, fire add_to_cart event immediately without waiting for cartId
      if (context.event === 'add_to_cart' && !isLoggedIn()) {
        let currentCartId = cartId;

        // Try reading cartId from cookie if not set
        if (!currentCartId) {
          currentCartId = cookieStore.get(`${siteId}-cart-helios`);
          if (currentCartId) {
            setCartId(currentCartId);
          }
        }

        if (currentCartId) {
          trackEvent(type, context, snowplowGlobalContextFields);
        } else {
          // Retry looking for cartId a few times
          let retries = 0;
          const maxRetries = 10;
          const interval = setInterval(() => {
            const cookieCartId = cookieStore.get(`${siteId}-cart-helios`);
            if (cookieCartId) {
              clearInterval(interval);
              setCartId(cookieCartId);
              trackEvent(type, context, snowplowGlobalContextFields);
            } else if (++retries >= maxRetries) {
              clearInterval(interval);
              console.warn('add_to_cart event dropped: cartId not available after retries');
            }
          }, 200); // Check every 200ms, up to ~2s total
        }

        return;
      }

      // All other events fire immediately
      trackEvent(type, context, snowplowGlobalContextFields);
    },
    [cartId, cartCount, locale, currentDevice, pageId, pcOptimumAccount, siteId]
  );
  /** The props necessary for the common contexts container */
  const commonContextProps = useMemo<ICommonContextsProps>(
    () => ({
      settings: {
        // ... map app settings data here as per the interface
        locale,
        env: process.env.NEXT_PUBLIC_VIEW_RESOLVER_ENV,
        isQuebec,
        banner: 'loblaw',
        sduiBaseUrl: 'https://dev-pcexpress.api.loblaw.digital/pcx-bff/api/v1',
        sduiApiKey: 'eZb87pkFiGAB7UAQbQbllgt7WAAk10rf',
        cdnBaseUrl: 'https://assets-platform.loblaws.ca/ldp-core-web-dev/lower/ldp/',
        componentError,
        setComponentError,
      },
      analytics: {
        trackEvent: trackEventWithCartIdRetry,
        trackRender: (componentName, context) => {
          trackRender(componentName);
        },
        trackEventTileAdClick: (
          componentName: string,
          { isSponsored, productId }: { isSponsored: boolean; productId: string }
        ) => {
          trackEventTileAdClick(componentName, { isSponsored, productId });
        },
      },
      featureFlags: {
        // ... map feature flags data here as per the interface
        EnableTestFeature: true,
        EnableHeliosSnowplow: true,
        isSearchTypeaheadEnabled,
      },
    }),
    [locale, currentDevice, cartCount, pageId, pcOptimumAccount, isQuebec, componentError]
  );

  return {
    locale,
    commonContextProps,
    cartCount,
    pageId,
    currentDevice,
    pcOptimumAccount,
    componentError,
  };
};

export default useCommonContextProps;
